import type React from 'react';

import { cn } from '@change/ds-utils';

import { withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/starterDashboardV2/dynamic-tasks.yml';

import { ModuleSeparator } from '#src/app/pages/starterDashboardV2/shared/components';
import { useDynamicModules } from '#src/app/pages/starterDashboardV2/shared/contexts';
import { usePetitionAiGuidanceExperiment } from '#src/app/pages/starterDashboardV2/shared/hooks';

import { NextSteps } from '../NextSteps';
import { DynamicTasksWithData as DynamicTasksBase } from '../NextSteps/components/DynamicTasks';
import { useDynamicTasks } from '../NextSteps/components/DynamicTasks/hooks/useDynamicTasks';

import { type AnimationPhase, useAnimationState } from './hooks/useAnimationState';

const DynamicTasks = withI18nPlaceholders(I18N_PLACEHOLDERS, DynamicTasksBase);

type NextStepsSectionProps = {
	position: 'top' | 'bottom';
	shouldShow: boolean;
	allTasksCompleted: boolean;
};

type AnimatedWrapperProps = {
	children: React.ReactNode;
	position: 'top' | 'bottom';
	phase: AnimationPhase;
};

type LegacyNextStepsSectionProps = Readonly<{
	position: 'top' | 'bottom';
	allTasksCompleted: boolean;
	isRendered: boolean;
	phase: AnimationPhase;
	isDynamicModuleLoaded: ReturnType<typeof useDynamicModules>['isDynamicModuleLoaded'];
}>;

const AnimatedWrapper: React.FC<AnimatedWrapperProps> = ({ children, position, phase }) => {
	const isEntering = phase === 'entering';
	const isExiting = phase === 'exiting';

	const getTranslateClass = () => {
		if (position === 'top' && isExiting) return 'motion-safe:translate-y-5';
		if (position === 'bottom' && isEntering) return 'motion-safe:-translate-y-5';
		return 'motion-safe:translate-y-0';
	};

	const getOpacityClass = () => {
		if (isExiting || (position === 'bottom' && isEntering)) return 'motion-safe:opacity-0';
		return 'motion-safe:opacity-100';
	};

	return (
		<div
			className={cn(
				'motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-in-out',
				getTranslateClass(),
				getOpacityClass(),
			)}
		>
			{children}
		</div>
	);
};

const LegacyNextStepsSection: React.FC<LegacyNextStepsSectionProps> = ({
	position,
	allTasksCompleted,
	isRendered,
	phase,
	isDynamicModuleLoaded,
}) => {
	const renderContent = () => (
		<AnimatedWrapper position={position} phase={phase}>
			<NextSteps />
		</AnimatedWrapper>
	);

	if (position === 'top') {
		return (
			<>
				{isRendered && renderContent()}
				{!allTasksCompleted && isDynamicModuleLoaded('NextSteps') && (
					<ModuleSeparator data-testid="next-steps-separator" />
				)}
			</>
		);
	}

	return (
		<>
			{allTasksCompleted && isDynamicModuleLoaded('NextSteps') && (
				<ModuleSeparator data-testid="next-steps-bottom-separator" />
			)}
			{isRendered && renderContent()}
		</>
	);
};

type DynamicNextStepsSectionProps = Readonly<{
	allTasksCompleted: boolean;
	isRendered: boolean;
	phase: AnimationPhase;
	isDynamicModuleLoaded: ReturnType<typeof useDynamicModules>['isDynamicModuleLoaded'];
}>;

const DynamicNextStepsSection: React.FC<DynamicNextStepsSectionProps> = ({
	allTasksCompleted,
	isRendered,
	phase,
	isDynamicModuleLoaded,
}) => {
	const dynamicTasks = useDynamicTasks();

	if (dynamicTasks.data.shouldShowFallbackNextSteps) {
		return (
			<LegacyNextStepsSection
				position="top"
				allTasksCompleted={allTasksCompleted}
				isRendered={isRendered}
				phase={phase}
				isDynamicModuleLoaded={isDynamicModuleLoaded}
			/>
		);
	}

	return (
		<>
			<DynamicTasks dynamicTasks={dynamicTasks} />
			<ModuleSeparator data-testid="dynamic-next-steps-separator" />
		</>
	);
};

export const NextStepsSection: React.FC<NextStepsSectionProps> = ({ position, shouldShow, allTasksCompleted }) => {
	const { isDynamicModuleLoaded } = useDynamicModules();
	const { isRendered, phase } = useAnimationState(shouldShow);
	const {
		data: { isEnabled: isDynamicTasksEnabled },
	} = usePetitionAiGuidanceExperiment();

	if (isDynamicTasksEnabled && position === 'bottom') {
		return null;
	}

	if (isDynamicTasksEnabled) {
		return (
			<DynamicNextStepsSection
				allTasksCompleted={allTasksCompleted}
				isRendered={isRendered}
				phase={phase}
				isDynamicModuleLoaded={isDynamicModuleLoaded}
			/>
		);
	}

	return (
		<LegacyNextStepsSection
			position={position}
			allTasksCompleted={allTasksCompleted}
			isRendered={isRendered}
			phase={phase}
			isDynamicModuleLoaded={isDynamicModuleLoaded}
		/>
	);
};
